import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useQuery } from 'js/hooks/useQuery';
import { SCHEMAS } from 'js/api/schemas';
import { workspaceRubricsQueryId } from 'js/store/reducers/queries';

const WORKSPACE_RUBRICS_IDLE_QUERY = 'workspace-rubrics/__idle';

/**
 * Workspace rubric index (`GET /workspaces/:id/rubrics`) — shared by the rubrics
 * list page and org manage rubric weight schemes.
 *
 * @param {string} workspaceId
 * @param {function} listWorkspaceRubrics — bound via `connect` (dispatches the thunk)
 */
export function useWorkspaceRubrics(workspaceId, listWorkspaceRubrics) {
  const queryId = useMemo(
    () =>
      workspaceId
        ? workspaceRubricsQueryId(workspaceId)
        : WORKSPACE_RUBRICS_IDLE_QUERY,
    [workspaceId]
  );

  const loadQuery = useCallback(
    (qid) => {
      if (!workspaceId || qid === WORKSPACE_RUBRICS_IDLE_QUERY) {
        return Promise.resolve(null);
      }
      return listWorkspaceRubrics(workspaceId, qid);
    },
    [listWorkspaceRubrics, workspaceId]
  );

  const { refresh, result, isLoading, hasLoaded } = useQuery({
    queryId,
    query: loadQuery,
    schema: SCHEMAS.RUBRIC_ARRAY,
  });

  useEffect(() => {
    refresh();
  }, [refresh]);

  const queryError = useSelector((state) => state.queries[queryId]?.error);

  const rubrics = useMemo(
    () => (Array.isArray(result) ? result : []),
    [result]
  );

  return {
    rubrics,
    queryId,
    refresh,
    isLoading,
    hasLoaded,
    error: queryError ?? null,
  };
}
